import { Component, DestroyRef, OnDestroy, OnInit, effect, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, Subscription, forkJoin, of, switchMap, take, tap } from 'rxjs';
import { SuppliersService } from 'settings/services/suppliers.service';
import { BaseModal } from 'shared/abstract/base-modal';
import { CurrentShijiUserInterface } from 'shared/interfaces/current-shiji-user.interface';
import { CurrentSupplierUserInterface, isCurrentSupplierUser } from 'shared/interfaces/current-supplier-user.interface';
import { ResourcePermission, UserGroupPermission } from 'shared/interfaces/permissions.interface';
import { EventBusService } from 'shared/services/event-bus.service';
import { FormDifferService } from 'shared/services/form-differ.service';
import { ToastService } from 'shared/services/toast.service';
import { UserApiService } from 'shared/services/userApi.service';
import { cleanGQLData } from 'shared/util/clean-GQL-data';
import { unsubscribeFromAll } from 'shared/util/unsubscribeFromAll';
import { VisualPermissionResponse } from 'users/interfaces/visual-permissions.interface';
import { UserGroupsService } from 'users/services/user-groups.service';
import { VisualsPermissionsService } from 'visuals/services/visuals-permissions.service';

@Component({
    selector: 'app-user-groups',
    templateUrl: './create-edit-user-groups.component.html',
    styleUrls: ['./create-edit-user-groups.component.scss'],
    standalone: false,
})
export class CreateEditUserGroupsComponent extends BaseModal implements OnInit, OnDestroy {
    public data!: any;
    public suppliers: any;
    public editUserGroupForm = new UntypedFormGroup({
        groupName: new UntypedFormControl('', [Validators.required, Validators.maxLength(64)]),
        permissions: new UntypedFormControl([[], []]),
    });
    public userType!: string;
    public isModalReadOnly: boolean = false;

    public get supplierId() {
        if (this.userIsAdmin) {
            return this.editUserGroupForm.get('companyName')?.value;
        } else {
            return this.data?.supplierId;
        }
    }

    visualPermissions = signal<VisualPermissionResponse[]>([]);
    userGroupHasAccessToVisualsArea = signal<boolean>(false);

    public userIsAdmin!: boolean;
    private subscriptions: { [key: string]: Subscription | undefined } = {};

    constructor(
        private eventBusService: EventBusService,
        private suppliersGridService: SuppliersService,
        private userGroupsGridService: UserGroupsService,
        private visualsPermissionsService: VisualsPermissionsService,
        private userApiService: UserApiService,
        private translate: TranslateService,
        private toastService: ToastService,
        private destroyRef: DestroyRef,
        private formDifferService: FormDifferService,
    ) {
        super();
        effect(() => {
            const hasVisualsUIEnabled = this.userGroupsGridService.userGroupHasVisualsUiEnabled();
            if (hasVisualsUIEnabled !== null) {
                this.userGroupHasAccessToVisualsArea.set(hasVisualsUIEnabled);
                return;
            }
            const userData = this.userApiService.currentUser;
            if (
                userData &&
                isCurrentSupplierUser(userData) &&
                userData.hasVisualsUIEnabled !== null &&
                userData.hasVisualsUIEnabled !== undefined
            ) {
                this.userGroupHasAccessToVisualsArea.set(userData.hasVisualsUIEnabled);
            }
            return false;
        });
    }

    public ngOnInit(): void {
        this.formDifferService.registerForm(this.editUserGroupForm);
        this.eventBusService
            .getObservableForEventStream('closeUserModal')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.closeModalInstance(undefined));

        this.userApiService
            .getLoginInformation()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((userInformation) => {
                this.userIsAdmin = userInformation.isShiji;

                const dataGatheringObservables: { [key: string]: Observable<any> } = {};

                if (this.userIsAdmin) {
                    dataGatheringObservables.getSuppliers = this.suppliersGridService.getAllSuppliers();

                    this.subscriptions.getSuppliers = dataGatheringObservables.getSuppliers.subscribe((suppliers) => {
                        this.suppliers = suppliers;
                    });

                    this.editUserGroupForm.addControl(
                        'companyName',
                        new UntypedFormControl(
                            {
                                // @ts-ignore
                                value: this.data.supplierId ?? userInformation.supplierId,
                                disabled: this.data.id || !userInformation.isShiji,
                            },
                            Validators.required,
                        ),
                    );

                    this.editUserGroupForm
                        .get('companyName')
                        ?.valueChanges.pipe(
                            takeUntilDestroyed(this.destroyRef),
                            tap(() => (this.isModalReadOnly = false)),
                        )
                        .subscribe((value) => {
                            const currentSupplier = this.suppliers.find((supplier: any) => supplier.id === value);
                            this.userGroupHasAccessToVisualsArea.set(currentSupplier?.hasVisualsUIEnabled || false);
                            this.editUserGroupForm.get('permissions')?.patchValue(null);
                            this.formDifferService.createDataSnapshot();
                        });
                } else {
                    // @ts-ignore
                    this.data.supplierId = userInformation.supplierId;
                }

                if (this.data.id) {
                    dataGatheringObservables.permissions = this.userGroupsGridService.getPermissionsByUserGroupId(this.data.id);

                    this.subscriptions.getPermissions = dataGatheringObservables.permissions.subscribe((permissions) => {
                        this.visualPermissions.set(permissions[2] || []);
                        this.editUserGroupForm.get('permissions')?.patchValue(cleanGQLData(permissions));
                    });
                } else {
                    this.editUserGroupForm.get('permissions')?.patchValue(null);
                }

                this.editUserGroupForm.get('groupName')?.setValue(this.data.name);
                this.subscriptions.forkJoin = forkJoin(Object.values(dataGatheringObservables)).subscribe(() =>
                    this.formDifferService.createDataSnapshot(),
                );
            });
    }

    public ngOnDestroy() {
        unsubscribeFromAll(this.subscriptions);
    }

    public saveUserGroupData() {
        this.editUserGroupForm.markAllAsTouched();
        this.editUserGroupForm.get('permissions')?.updateValueAndValidity();

        // TODO: Do nothing if already saved and show a toast / disable the save button
        if (!this.editUserGroupForm.valid) {
            return;
        }

        if (this.subscriptions.saveFinished) {
            this.subscriptions.saveFinished.unsubscribe();
        }

        let operationType: 'create' | 'update';
        let successMessage: string;

        // Determine if it's an update or save
        if (this.data.id) {
            successMessage = this.translate.instant('users.userGroups.createEdit.groupUpdate', {
                groupName: this.editUserGroupForm.value.groupName,
            });
            operationType = 'update';
        } else {
            successMessage = this.translate.instant('users.userGroups.createEdit.groupCreate', {
                groupName: this.editUserGroupForm.value.groupName,
            });
            operationType = 'create';
        }

        this.subscriptions.saveFinished = this[`${operationType}UserGroup`]()
            .pipe(
                switchMap(({ data, statusCode }) => {
                    if (statusCode?.toLowerCase() === 'conflict') {
                        this.toastService.showToast('users.userGroups.createEdit.alreadyExists', 'error', {
                            groupName: this.editUserGroupForm.value.groupName,
                        });
                        return EMPTY;
                    }

                    const userGroupId = data?.userGroupId ?? data;

                    return this[`${operationType}Permissions`](userGroupId).pipe(
                        switchMap(() => this.saveVisualPermissions(operationType, userGroupId)),
                    );
                }),
            )
            .subscribe((visualPermissionsSaved) => {
                if (visualPermissionsSaved) {
                    this.toastService.showToast(successMessage, 'success');
                }

                this.closeModalInstance(true);
            });
    }

    /**
     * Saves the visual permissions after the group and its other permissions, and reports whether it worked,
     * so a failed visual save can no longer be reported as a success.
     */
    private saveVisualPermissions(operationType: 'create' | 'update', userGroupId: string): Observable<boolean> {
        if (!this.visualsPermissionsService.visualsSectionActive()) {
            return of(true);
        }

        return new Observable<boolean>((subscriber) => {
            const subscription = this.visualsPermissionsService.saveCompleted.pipe(take(1)).subscribe(subscriber);

            if (operationType === 'create') {
                this.visualsPermissionsService.createUserGroupVisualPermissionsId.set(userGroupId);
            } else {
                this.eventBusService.dispatch('closeUserGroupModal', { operationType });
            }

            return () => subscription.unsubscribe();
        });
    }

    public closeModal(result: boolean): void {
        this.closeModalInstance(result);
    }

    private createUserGroup(): Observable<any> {
        if (!this.editUserGroupForm.value.companyName) {
            return this.userApiService.getLoginInformation().pipe(
                switchMap((userData: CurrentSupplierUserInterface | CurrentShijiUserInterface) => {
                    userData = userData as CurrentSupplierUserInterface;
                    const supplierId = userData.supplierId;

                    return this.userGroupsGridService.createUserGroup({
                        supplierId: supplierId as string,
                        name: this.editUserGroupForm.value.groupName,
                    });
                }),
            );
        }

        return this.userGroupsGridService.createUserGroup({
            supplierId: this.editUserGroupForm.value.companyName,
            name: this.editUserGroupForm.value.groupName,
        });
    }

    private updateUserGroup(): Observable<any> {
        return this.userGroupsGridService.updateUserGroup({ id: this.data.id, ...this.editUserGroupForm.value });
    }

    private updatePermissions(): Observable<any> {
        const permissionObject: any = this.userGroupsGridService.separateResourcePermissionRequests(
            this.editUserGroupForm.value.permissions,
            (this.formDifferService.getSnapshotData() as any).permissions,
        );
        const saveObservables: Observable<any>[] = [];

        ['userGroupResourcePermissions', 'userGroupPermissions'].forEach((permissionType) => {
            ['create', 'update', 'delete'].forEach((action) => {
                if (permissionObject[permissionType]?.[action].length) {
                    const functionName = action + permissionType.capitalizeFirstLetter();
                    const serviceFunction = (this.userGroupsGridService as any)[functionName].bind(this.userGroupsGridService);
                    const individualPermission = permissionObject[permissionType][action];

                    saveObservables.push(serviceFunction(individualPermission));
                }
            });
        });

        return saveObservables.length ? forkJoin(saveObservables) : of(undefined);
    }

    private createPermissions(userGroupId: string): Observable<any> {
        const permissions = this.editUserGroupForm.value.permissions;

        if (permissions === null) {
            return of(undefined);
        }

        const resourcePermissions = permissions[0].map((item: ResourcePermission) => {
            item.userGroupId = userGroupId;
            return item;
        });
        const userGroupPermissions = permissions[1].map((item: UserGroupPermission) => {
            item.userGroupId = userGroupId;
            return item;
        });
        const createResourcePermissions = this.userGroupsGridService.createUserGroupResourcePermissions(resourcePermissions);
        const createUserGroupPermissions = this.userGroupsGridService.createUserGroupPermissions(userGroupPermissions);

        return forkJoin([createUserGroupPermissions, createResourcePermissions]);
    }
}
